import {
  BRIDAL_SKU_SUFFIX,
  CAMPAIGN_SKU_SUFFIX,
  SUB_SKU_SUFFIX,
} from '@/constants/commerce'
import {
  EARLIEST_MODIFIABLE_WEEK,
  EARLIEST_NEW_PROGRAM_WEEK,
  LATEST_MODIFIABLE_MEAL_SWAP_WEEK,
  LATEST_MODIFIABLE_WEEK,
} from '@/constants/program-dates'
import type { Subscription } from '@/models/subscription'
import type { Subscription as PopsSubscription } from '@/types/pops'
import type { Attribute, OrderLineItem } from '@/types/shopify'

import type { Dayjs } from './dayjs-config'
import dayjs from './dayjs-config'

// Matches and captures parts of a meal program SKU. Currently, meal program SKUs could look like any
// of the following: 3BLD, 3BLD$SUB, 3BLD_NYLA, SYS_5BLD_MET
const SKU_REGEX =
  /^(?<prefix>[A-Z]*)?(?:_?)(?<plan>(?<daysOfFoodString>\d+)(?<mealsString>BLD|BL|BD|LD|B|L|D))(?:_?)(?<suffix>.*)/

export const isProductSubscription = (
  subscription: Subscription | PopsSubscription,
) => {
  return subscription.sku.startsWith('CB-')
}

export const isMealProgramSubscription = (
  subscription: Subscription | PopsSubscription,
) => {
  return !isProductSubscription(subscription)
}

export const getWeeksArray = (startWeek: Dayjs, numberOfWeeks: number) => {
  return Array.from({ length: numberOfWeeks }, (_value, index) =>
    startWeek.add(index, 'weeks'),
  )
}

export const isWeekAfterCutoff = (week: Dayjs) => {
  return week.isSameOrAfter(EARLIEST_MODIFIABLE_WEEK, 'day')
}

export const getDaysOfFoodFromSku = (sku: string) => {
  return Number(SKU_REGEX.exec(sku)?.groups?.daysOfFoodString)
}

export const getDaysOfFoodArrayFromSku = (sku: string): string[] => {
  const daysOfFood = getDaysOfFoodFromSku(sku)

  const dayAssignments: Record<number, string[]> = {
    2: ['thursday', 'friday'],
    3: ['monday', 'wednesday'],
    5: ['monday', 'friday'],
  }

  return dayAssignments[daysOfFood] || []
}
export const getPlanFromSku = (sku: string) => {
  return SKU_REGEX.exec(sku)?.groups?.plan
}

export const getMealsStringFromSku = (sku: string) => {
  return SKU_REGEX.exec(sku)?.groups?.mealsString
}

export const getMealsArrayFromSku = (sku: string) => {
  const mealsString = getMealsStringFromSku(sku)
  return getMealsArrayFromMealsString(mealsString)
}

export const getMealsArrayFromMealsString = (mealsString?: string) => {
  const meals = []

  if (mealsString?.includes('B')) meals.push('breakfast')
  if (mealsString?.includes('L')) meals.push('lunch')
  if (mealsString?.includes('D')) meals.push('dinner')

  return meals
}

export const getSuffixFromSku = (sku: string) => {
  return SKU_REGEX.exec(sku)?.groups?.suffix
}

export const buildMealIdForSku = (meals: string[]) => {
  let mealSku = ''

  if (meals.includes('breakfast')) mealSku = mealSku.concat('B')
  if (meals.includes('lunch')) mealSku = mealSku.concat('L')
  if (meals.includes('dinner')) mealSku = mealSku.concat('D')

  return mealSku
}

export const formatMeals = (meals: any, delimiter = ' ') => {
  const mealsSorted = []

  if (meals.includes('breakfast')) mealsSorted.push('Breakfast')
  if (meals.includes('lunch')) mealsSorted.push('Lunch')
  if (meals.includes('dinner')) mealsSorted.push('Dinner')

  return mealsSorted.join(delimiter)
}

export const formatDate = (date: any, format: any) => {
  if (date) {
    return dayjs(date).format(format)
  }

  return ''
}

export const buildSku = (
  daysOfFood: number,
  meals: string[],
  suffix?: string,
) => {
  const mealsAcronym = buildMealIdForSku(meals)
  return `${daysOfFood}${mealsAcronym}${suffix ? '_' + suffix : ''}`
}

export const isWeekTooFarForMealSwaps = (week: Dayjs) => {
  return week.isAfter(LATEST_MODIFIABLE_MEAL_SWAP_WEEK, 'week')
}

export const isWeekTooFarToModify = (week: Dayjs) => {
  return week.isAfter(LATEST_MODIFIABLE_WEEK, 'week')
}

export const isAddonLineItem = (sku: string) => {
  const addonSkuRegex = new RegExp(/ADDON/)

  return addonSkuRegex.test(sku)
}

export const isRewardProduct = (sku: string) => {
  return sku.endsWith('$RW')
}

export const isGWP = (sku: string) => {
  return sku.endsWith('$GWP')
}

export const rootFeedProductType = (productType: string) => {
  if (!productType) return ''

  switch (productType) {
    case 'OMD':
      return 'NP'
    case 'Clean Boutique':
      return 'BP'
    default:
      return productType
  }
}

export const getStartDate = (lineItem: OrderLineItem) => {
  const startDateAttribute = lineItem.customAttributes?.find(
    (attribute: Attribute) => attribute.key === '_start_date',
  )

  return dayjs(startDateAttribute?.value).format('MMMM D, YYYY')
}

export const isCleanseLineItem = (sku: string) => {
  return sku.includes(CAMPAIGN_SKU_SUFFIX)
}

export const isBridalLineItem = (sku: string) => {
  return sku.includes(BRIDAL_SKU_SUFFIX)
}

export const getProductTypeFromLineItem = (lineItem: any) => {
  return lineItem?.variant?.product?.productType
}

export const isMealProgramLineItem = (lineItem: any) => {
  return getProductTypeFromLineItem(lineItem) === 'OMD'
}

export const isCleanBoutiqueLineItem = (lineItem: any) => {
  return getProductTypeFromLineItem(lineItem) === 'Clean Boutique'
}

export const isItemPossibleSubscription = (orderLineItem: any) => {
  return orderLineItem?.variant?.sku.endsWith(SUB_SKU_SUFFIX)
}

export const formatDeliveryWindow = (
  deliveryWindow: string[] | null,
  format: string = '[start]-[end]',
  alwaysShowTimeOfDay: boolean = false,
): string => {
  if (!deliveryWindow) return ''

  let beginning = dayjs(deliveryWindow[0], 'HH:mm')
  let ending = dayjs(deliveryWindow[1], 'HH:mm')

  let formattedBeginning: string
  let formattedEnding: string = ending.format('hA')

  if (alwaysShowTimeOfDay || beginning.format('A') !== ending.format('A')) {
    formattedBeginning = beginning.format('hA')
  } else {
    formattedBeginning = beginning.format('h')
  }

  return format
    .replace('[start]', formattedBeginning)
    .replace('[end]', formattedEnding)
}

export const DELIVERY_ADDRESS_ERROR_MESSAGES: Record<string, string> = {
  'Address Not Found.': 'The delivery address entered could not be found',
  'Invalid State Code.': 'The state code entered is invalid',
  'Invalid City.': 'The city entered is invalid',
  'Invalid Zip Code.': 'The zip code entered is invalid',
  'Delivery address can not be a PO BOX':
    'Currently we cannot ship to P.O. boxes',
  'The zip code does not match the one given with the address':
    'The zip code entered is invalid',
}

export const getWeekOfDate = (date: string) => {
  return dayjs(date).startOf('week').format('YYYY-MM-DD')
}

export const isDateAfterNewProgramWeekCutoff = (date: Dayjs): boolean => {
  return date.isSameOrAfter(EARLIEST_NEW_PROGRAM_WEEK, 'day')
}

export const isMealProgramStartDateValid = (startDate: string): boolean => {
  const date = dayjs(startDate)
  const isDateAfterCutoff = isDateAfterNewProgramWeekCutoff(date)

  return isDateAfterCutoff
}
